// SoundManager.ts
import { Howl } from "howler";
import * as THREE from "three";

// Define the names of your sounds
export type GlobalSoundNames = "turn_safe" | "turn_lose" | "turn_dead" | "turn_win" | "flip_all_cards";
export type PositionalSoundsNames = "card_cut" | "card_slide" | "card_flip" | "card_deal";

// Map of Howler sounds
type HowlerSounds = {
  [key in GlobalSoundNames]: Howl;
};

const audioPath = "/audio/";
const wav = ".wav";

const audioUri = (name: string) => {
  return audioPath + name + wav;
}
const howlUri = (name: string) => {
  return new Howl({src: [audioPath + name + wav]});
}

class SoundManager {
  private globalSounds: Partial<HowlerSounds> = {};
  private positionalSounds: Partial<Record<PositionalSoundsNames, string[]>> = {};
  private audioListener: THREE.AudioListener | null = null;
  private currentAmbient: Howl | null = null;
  private muted = false;

  // Initialize with camera listener
  public init(listener: THREE.AudioListener) {
    this.audioListener = listener;

    // Load global (non-positional) sounds
    this.globalSounds = {
      turn_safe: howlUri("turn_safe"),
      turn_dead: howlUri("turn_dead"),
      turn_lose: howlUri("turn_lose"),
      turn_win: howlUri("turn_win"),
      flip_all_cards: howlUri("card_flip_many")
    };
    this.positionalSounds = {
      // 3D sounds just store URLs
      "card_cut": [audioUri("card_cut1"), audioUri("card_cut2")],
      "card_slide": [audioUri("card_slide1"),audioUri("card_slide2"), audioUri("card_slide3")],
      "card_flip": [audioUri("card_flip1"), audioUri("card_flip2")],
      "card_deal": [audioUri("card_deal1"), audioUri("card_deal2"), audioUri("card_deal3")]
    }
  }

  // Play non-positional (UI / global) sound
  public playGameSound(name: GlobalSoundNames) {
    if (this.muted) return;
    const sound = this.globalSounds[name] as Howl | undefined;
    if (!sound) {
      console.warn(`Sound '${name}' not found`);
      return;
    }
    sound.play();
  }

  // Play positional 3D sound attached to an object
  public play3DGameSound(name: PositionalSoundsNames, object3D: THREE.Object3D | null) {
    if (!object3D || this.muted) return;
    
    if (!this.audioListener) {
      console.warn("AudioListener not initialized");
      return;
    }

    const variants = this.positionalSounds[name];
    if (!Array.isArray(variants) || variants.length === 0) return;

    const soundUrl = variants[Math.floor(Math.random() * variants.length)];
    if (typeof soundUrl !== "string") {
      console.warn(`3D sound '${name}' not found`);
      return;
    }

    const audio = new THREE.PositionalAudio(this.audioListener);
    const loader = new THREE.AudioLoader();

    loader.load(soundUrl, (buffer) => {
      audio.setBuffer(buffer);
      audio.setRefDistance(1);
      audio.setDistanceModel("linear");
      audio.setRolloffFactor(1.5);
      audio.setVolume(1);

      audio.setPlaybackRate(0.95 + Math.random() * (1.05 - 0.95));
      audio.play();

      object3D.add(audio);

      // Clean up after playback
      audio.onEnded = () => {
        object3D.remove(audio);
        audio.disconnect();
      };
    });
  }

  public setMuted(muted: boolean) {
    this.muted = muted;

    // mute all Howler sounds
    Howler.mute(muted);

    if (muted) this.stopAmbient();
    else this.playAmbient();

    // mute positional sounds
    if (this.audioListener) {
      this.audioListener.setMasterVolume(muted ? 0 : 1);
    }
  }

  public toggleMute() {
    this.setMuted(!this.muted);
  }

  public isMuted() {
    return this.muted;
  }

  public playAmbient() {
    const ambient = new Howl({src: ['/audio/ambience.ogg'], loop: true, volume: 0.2});
    console.log('ambi', ambient);
    if (!ambient) return;

    if (this.currentAmbient === ambient) return;

    this.stopAmbient();

    ambient.play();
    this.currentAmbient = ambient;
  }

  public stopAmbient() {
    if (!this.currentAmbient) return;

    this.currentAmbient.stop();
    this.currentAmbient = null;
  }

  public setAmbientVolume(volume: number) {
    if (this.currentAmbient) {
      this.currentAmbient.volume(volume);
    }
  }
}

export default new SoundManager();
